import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { FormikConfig, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { DialogActions, DialogBody, DialogTitle, makeStyles, mergeClasses } from '@fluentui/react-components'
import { bundleIcon, DeleteFilled, DeleteRegular, DismissFilled, DismissRegular, SaveFilled, SaveRegular } from '@fluentui/react-icons'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { NoteMutationDto, NoteOverviewDto } from '../../../../config/noteConfig'
import NoteMutationDialogBodyContent from './NoteMutationDialogBodyContent'
import TooltippedButton from '../../Button/TooltippedButton'
import useAxios from '../../../../hooks/useAxios'
import { useApplicationStyles } from '../../../../pages/App'
import { useCounter } from 'usehooks-ts'

const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)

export type NoteDialogBodyProps = BaseMutableOverviewDialogProps<NoteOverviewDto>

export type NoteMutationDialogProps = HammockFormDialogProps<NoteMutationDto, NoteDialogBodyProps>

const useStyles = makeStyles({
    actions: { width: '100%' },
})

/**
 * Add/edit dialog for a note (HAM-3476). The ordinary Cancel/Save dialog: saving while typing is a follow-up
 * (HAM-4337). Deleting asks for confirmation, which happens with a MessageBar in the body rather than a second
 * dialog - a dialog inside a dialog traps focus twice.
 */
export default function NoteMutationDialogBody(props: NoteMutationDialogProps): ReactElement {
    const axios = useAxios()
    const styles = useStyles()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()
    const { submitForm, isSubmitting, resetForm } = useFormikContext<NoteMutationDto>()

    const [confirmDelete, setConfirmDelete] = useState<boolean>(false)
    // Bumped on open so the cursor lands in the title (AC 2).
    const { count: focusTrigger, increment: updateFocusTrigger } = useCounter(0)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as NoteDialogBodyProps), [props])
    const id = dialogBodyProps.id

    const handleSubmit = useCallback<FormikConfig<NoteMutationDto>['onSubmit']>(
        note => (id ? axios.put(`/note/${id}`, note) : axios.post('/note', note)).then(() => resetForm()),
        [id],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    useEffect(() => updateFocusTrigger(), [])

    const handleDelete = () =>
        axios.delete(`/note/${id}`).then(() => {
            resetForm()
            props.onDismiss?.()
        })

    // Cancel is the explicit way out: it discards without asking. Escape and a click next to the dialog do not close
    // a form with unsaved changes at all; the body shows a message pointing here instead.
    const handleCancel = () => {
        resetForm()
        props.onDismiss?.()
    }

    return (
        <DialogBody>
            <DialogTitle>{translate(id ? 'file.pivot.note.edit.dialog.edit' : 'file.pivot.note.edit.dialog.create')}</DialogTitle>
            <NoteMutationDialogBodyContent
                {...props}
                focusTrigger={focusTrigger}
                confirmDelete={confirmDelete}
                onConfirmDelete={handleDelete}
                onCancelDelete={() => setConfirmDelete(false)}
            />
            <DialogActions>
                <div className={mergeClasses(styles.actions, appStyles.spaceBetween)}>
                    {id ? (
                        <TooltippedButton
                            appearance="subtle"
                            display="iconAndLabel"
                            label={translate('action.delete')}
                            icon={<DeleteIcon />}
                            disabled={isSubmitting || confirmDelete}
                            onClick={() => setConfirmDelete(true)}
                        />
                    ) : (
                        <div></div>
                    )}
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <TooltippedButton
                            appearance="secondary"
                            label={translate('action.cancel')}
                            icon={<CloseIcon />}
                            disabled={isSubmitting}
                            onClick={handleCancel}
                        />
                        <TooltippedButton
                            appearance="primary"
                            label={translate(isSubmitting ? 'base.saving' : 'action.save')}
                            icon={<SaveIcon />}
                            disabled={isSubmitting}
                            onClick={() => submitForm()}
                        />
                    </div>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
